import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Hash, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface ProjectCodeModalProps {
  open: boolean;
  onClose: () => void;
  projectName: string;
  currentCode: string;
  onSave: (code: string) => Promise<void>;
}

export function ProjectCodeModal({
  open,
  onClose,
  projectName,
  currentCode,
  onSave,
}: ProjectCodeModalProps) {
  const { t } = useTranslation('common');
  const [code, setCode] = useState(currentCode);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(currentCode);
    }
  }, [open, currentCode]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(code.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCode(currentCode);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1.5">
          <DialogTitle className="text-lg flex items-center gap-2">
            <Hash className="h-5 w-5 text-primary" />
            Código da Obra
          </DialogTitle>
          <DialogDescription className="text-sm">
            Configure o código para o projeto <strong>{projectName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="project-code" className="text-sm">
              Código da obra ({t('optional')})
            </Label>
            <Input
              id="project-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Ex: GC081, OBR-2024-001"
              maxLength={50}
              autoFocus
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              {code.length}/50 {t('characters')}
            </p>
          </div>

          {/* Dica */}
          <div className="flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
            <FileText className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900 w-full">
              <p className="font-medium mb-1">Para que serve?</p>
              <p className="mb-1.5">
                O código será usado como prefixo automático no nome dos arquivos PDF exportados.
              </p>
              <p className="font-medium mb-0.5">Exemplo de nome do arquivo:</p>
              <span className="font-mono bg-blue-100 px-1.5 py-0.5 rounded break-all">
                {(code.trim() || 'PROJETO')}_26040101_ATA_REUNIAO_Nº1.pdf
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            size="sm"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            size="sm"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-foreground mr-1.5"></div>
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
